/**
 * Resume Upload Panel — Iter-224c.
 *
 * Lets a talent upload, replace, view, or remove their resume. Accepts
 * PDF / DOC / DOCX up to 5 MB. Backend stores in Cloudinary as a raw
 * resource and saves the URL on talent_profiles.resume_url.
 *
 * Anthony Piazza reported he couldn't upload his resume — the feature
 * literally didn't exist until this iteration.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { FileText, Upload, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

const ResumePanel = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [resume, setResume] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/profile/resume');
      setResume(res.data?.resume_url ? res.data : null);
    } catch (e) {
      // Endpoint will return null when nothing uploaded — quiet failure.
      setResume(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('Resume must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      toast.error('Upload a PDF, DOC, or DOCX file.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/profile/resume', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResume(res.data);
      toast.success('Resume uploaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resume upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete('/profile/resume');
      setResume(null);
      toast.success('Resume removed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove resume.');
    }
  };

  return (
    <Card data-testid="resume-panel">
      <CardHeader className="py-3 px-5">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" /> Resume
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5 space-y-3">
        <p className="text-xs text-gray-500">
          Upload a PDF, DOC, or DOCX (max 5 MB). Clients and admins reviewing your profile will be
          able to view and download it.
        </p>

        {loading ? (
          <div className="text-xs text-gray-400 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : resume?.resume_url ? (
          <div className="flex items-center gap-2 flex-wrap" data-testid="resume-current">
            <a
              href={resume.resume_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-700 hover:underline text-sm flex items-center gap-1.5 truncate max-w-[260px]"
              data-testid="resume-current-link"
            >
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              {resume.resume_filename || 'Current resume'}
            </a>
            <label className="inline-flex">
              <input type="file" className="hidden" accept={ACCEPT} onChange={handleUpload} disabled={uploading} data-testid="resume-replace-input" />
              <Button size="sm" variant="outline" disabled={uploading} asChild>
                <span className="cursor-pointer">{uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Replace'}</span>
              </Button>
            </label>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={uploading}
              data-testid="resume-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          </div>
        ) : (
          <label className="inline-flex">
            <input type="file" className="hidden" accept={ACCEPT} onChange={handleUpload} disabled={uploading} data-testid="resume-upload-input" />
            <Button size="sm" disabled={uploading} asChild data-testid="resume-upload-btn">
              <span className="cursor-pointer">
                {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                Upload Resume
              </span>
            </Button>
          </label>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumePanel;
